import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip as ReTooltip } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Mail, Phone, MapPin, Calendar, IdCard, Bluetooth, CreditCard, Car,
  Pencil, BookOpen, AlertTriangle, CheckCircle, Ban, Loader2, ShieldAlert,
  History, MessageCircle, Layers, Link2, Gauge, Route, Paperclip, RefreshCw, ExternalLink, Clock, Download, Upload,
} from 'lucide-react';
import { ReadOnlyAttachmentsList } from '../shared/Attachments';
import { exportDashboardPdf, exportDashboardHtml, safeName } from '../../utils/tripExport';
import { resolveGroupTextColor } from '../shared/groupColors';
import { DrivingBehaviourHelp } from '../fleet/DrivingBehaviourHelp';
import { PaginationFooter } from '../shared/PaginationFooter';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatDT, formatWallDateOnly, formatTimeAgo, formatDuration } from '../../utils/timezone';

// License expiry health (shared with the People tables): expired / expiring ≤30d.
export const licenseExpiryInfo = (expiry) => {
  if (!expiry) return null;
  const d = new Date(expiry);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(23, 59, 59, 999);
  const days = Math.ceil((d.getTime() - Date.now()) / 86400000);
  if (days < 0) return { label: 'License expired', cls: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400' };
  if (days <= 30) return { label: `License expires in ${days}d`, cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400' };
  return null;
};

const SignupBadge = ({ status }) => {
  if (status === 'pending_invitation') return <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 gap-1"><Mail className="w-3 h-3" /> Sign-up pending</Badge>;
  if (status === 'active') return <Badge variant="outline" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 gap-1"><CheckCircle className="w-3 h-3" /> Signed up</Badge>;
  if (status === 'deactivated') return <Badge variant="outline" className="bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400 gap-1"><Ban className="w-3 h-3" /> Login disabled</Badge>;
  return <Badge variant="outline" className="text-muted-foreground gap-1">No account</Badge>;
};

const Row = ({ icon: Icon, label, value, testid, href }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={testid}>
    <Icon className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
    <span className="text-muted-foreground w-28 shrink-0">{label}</span>
    {href && value ? (
      <a href={href} className="min-w-0 break-words text-[#2563eb] dark:text-blue-400 hover:underline">{value}</a>
    ) : (
      <span className="min-w-0 break-words">{value || '-'}</span>
    )}
  </div>
);

const fmtDT = (v) => (v ? formatDT(v) : '-');
const parseTs = (t) => { try { return new Date(String(t).replace('Z', '+00:00')).getTime(); } catch { return 0; } };

const AUDIT_LABELS = {
  created: 'Created', updated: 'Updated', deleted: 'Deleted', status_changed: 'Status changed',
  invite_resent: 'Invite re-sent', attachment_added: 'Document added', attachment_removed: 'Document removed',
  attachment_updated: 'Document updated', impersonated: "Accessed via 'Login as'",
  logged_out: 'Logged out', impersonation_ended: "'Login as' session ended",
  photo_updated: 'Photo updated', photo_removed: 'Photo removed', logged_in: 'Logged in',
};

// Driving Behaviour score bands (shared with People table badges): green 85+, amber 60–84, red <60.
export const behaviourScoreCls = (score) => (score == null ? 'bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400'
  : score >= 85 ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
  : score >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
  : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400');

const HB_LABELS = { harsh_braking: 'Harsh Braking', harsh_acceleration: 'Harsh Acceleration', harsh_cornering: 'Harsh Cornering' };
// Same event colours + donut as the Vehicle Dashboard's Driving Behaviour card.
const HB_COLORS = { harsh_braking: '#ef4444', harsh_acceleration: '#f59e0b', harsh_cornering: '#8b5cf6' };
const TT_PROPS = {
  contentStyle: { backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' },
  labelStyle: { color: 'hsl(var(--foreground))', fontWeight: 600 },
  itemStyle: { color: 'hsl(var(--foreground))' },
  wrapperStyle: { zIndex: 50, outline: 'none' },
};

const chipCls = 'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium';

// Dashboard-page-style KPI card with a coloured icon tile (matches the Vehicle Dashboard).
const KpiCard = ({ label, value, sub, icon: Icon, color, testId }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={testId}>
    <CardContent className="p-3.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="text-lg font-bold leading-tight mt-0.5">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground truncate" data-testid={`${testId}-sub`}>{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg shrink-0 ${color}`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const SectionCard = ({ icon: Icon, title, right, children, testId, className }) => (
  <Card data-testid={testId} className={className}>
    <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-sm flex items-center gap-2"><Icon className="w-4 h-4 text-[#2563eb]" /> {title}</CardTitle>
      {right}
    </CardHeader>
    <CardContent className="px-4 pb-4 pt-0">{children}</CardContent>
  </Card>
);

export const PersonProfileDrawer = ({ open, onOpenChange, person, personType, api, onEdit, onLogBook, onLocation, onObservedHistory, groups = [] }) => {
  const navigate = useNavigate();
  const { features, isSubpageEnabled } = useFeatureFlags();
  const greenDrivingOn = isSubpageEnabled('fleet_green_driving');
  const { user: currentUser } = useAuth();
  const [recent, setRecent] = useState({ loading: false, entries: [] });
  const [audit, setAudit] = useState({ loading: false, entries: [], total: 0 });
  const [auditPage, setAuditPage] = useState(1);
  const [auditPageSize, setAuditPageSize] = useState(10);
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => { setPhoto(person?.photo || null); }, [person?.id, person?.photo]);
  const attachments = person?.attachments || [];

  // Auto-refresh every 60s while open — same cadence as the Vehicle Dashboard.
  useEffect(() => {
    if (!open) return undefined;
    const iv = setInterval(() => setRefreshNonce((n) => n + 1), 60000);
    return () => clearInterval(iv);
  }, [open]);

  useEffect(() => {
    if (!open || !photo?.url) { setPhotoUrl(null); return undefined; }
    let cancelled = false; let obj = null;
    api.get(photo.url.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((r) => { if (!cancelled) { obj = URL.createObjectURL(r.data); setPhotoUrl(obj); } })
      .catch(() => { if (!cancelled) setPhotoUrl(null); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, photo?.url]);

  useEffect(() => { setAuditPage(1); }, [open, person?.id]);

  const [behaviour, setBehaviour] = useState(null);
  useEffect(() => {
    const eligible = greenDrivingOn && (personType === 'driver' || (personType === 'staff' && person?.driver_eligible));
    if (!open || !person?.id || !eligible) { setBehaviour(null); return undefined; }
    let cancelled = false;
    api.get(`/people/driver/${person.id}/driving-behaviour?days=30`)
      .then((r) => { if (!cancelled) setBehaviour(r.data?.eligible === false ? null : r.data); })
      .catch(() => { if (!cancelled) setBehaviour(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id, person?.driver_eligible, personType, refreshNonce, greenDrivingOn]);

  // 30d trips/distance for people without a behaviour score (passengers, non-eligible staff).
  const [tripStats, setTripStats] = useState(null);
  useEffect(() => {
    const hasBehaviour = greenDrivingOn && (personType === 'driver' || (personType === 'staff' && person?.driver_eligible));
    if (!open || !person?.id || hasBehaviour) { setTripStats(null); return undefined; }
    let cancelled = false;
    api.get(`/people/${personType}/${person.id}/trip-stats?days=30`)
      .then((r) => { if (!cancelled) setTripStats(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id, person?.driver_eligible, personType, refreshNonce, greenDrivingOn]);

  useEffect(() => {
    if (!open || !person?.id) return undefined;
    let cancelled = false;
    setAudit((prev) => ({ ...prev, loading: true }));
    api.get(`/people/${personType}/${person.id}/audit?limit=${auditPageSize}&skip=${(auditPage - 1) * auditPageSize}`)
      .then((r) => {
        if (cancelled) return;
        const headerTotal = parseInt(r.headers?.['x-total-count'], 10);
        const total = Number.isFinite(headerTotal) ? headerTotal : (r.data?.total ?? (r.data?.entries || []).length);
        setAudit({ loading: false, entries: r.data?.entries || [], total });
      })
      .catch(() => { if (!cancelled) setAudit({ loading: false, entries: [], total: 0 }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id, personType, auditPage, auditPageSize, refreshNonce]);

  useEffect(() => {
    if (!open || !person?.id) return undefined;
    let cancelled = false;
    setRecent((prev) => ({ ...prev, loading: prev.entries.length === 0 }));
    api.get(`/people/${personType}/${person.id}/logbook?limit=5&skip=0`)
      .then((r) => { if (!cancelled) { setRecent({ loading: false, entries: r.data?.entries || [] }); setLastUpdated(Date.now()); } })
      .catch(() => { if (!cancelled) setRecent({ loading: false, entries: [], error: true }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id, personType, refreshNonce]);

  const [binding, setBinding] = useState(null);
  useEffect(() => { setBinding(person?.current_binding || null); }, [person?.id, person?.current_binding]);
  // The Refresh button / 60s auto-refresh re-reads the person so the "Driving …" pill stays live.
  useEffect(() => {
    if (!open || !person?.id || refreshNonce === 0) return undefined;
    let cancelled = false;
    const path = { driver: 'drivers', staff: 'staff', passenger: 'passengers' }[personType];
    if (!path) return undefined;
    api.get(`/${path}/${person.id}`)
      .then((r) => { if (!cancelled) setBinding(r.data?.current_binding || null); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, person?.id, personType, refreshNonce]);

  if (!person) return null;
  const expInfo = licenseExpiryInfo(person.license_expiry);
  const chatTargetId = person.linked_user_id || person.chat_user_id;
  const canMessage = features?.pages?.chat_page === true && !!chatTargetId
    && chatTargetId !== currentUser?.id
    && (person.linked_user_id
      ? true
      : (!!person.account_enabled && !!person.messaging_enabled
        && !['pending_invitation', 'deactivated', 'expired'].includes(person.chat_invite_status)));

  const openChat = async () => {
    try {
      const r = await api.post('/conversations', { type: 'direct', participants: [chatTargetId] });
      onOpenChange(false);
      navigate(`/chat?conversation=${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not open chat');
    }
  };

  const initials = (person?.full_name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  const bindingLabel = binding
    ? (binding.role === 'driving' ? 'Driving' : personType === 'passenger' ? 'Passenger in' : 'In')
    : null;
  const openVehicle = () => {
    if (!binding?.vehicle_device_id) return;
    onOpenChange(false);
    navigate(`/fleet?qv=${binding.vehicle_device_id}`);
  };

  const hbRatingP = behaviour?.rating || 'rated';

  // PDF / HTML dashboard export — same branded look as the History page exports.
  const buildExportSections = () => {
    const dash = (v) => (v == null || v === '' ? '-' : String(v));
    const two = { columns: [{ label: 'Field', weight: 1 }, { label: 'Value', weight: 2.5 }] };
    const sections = [{
      heading: 'Details', ...two,
      rows: [
        ['Name', dash(person.full_name)], ['Type', personType], ['Status', dash(person.status)],
        ['Email', dash(person.email)], ['Phone', dash(person.phone)], ['Address', dash(person.address)],
        ['Date of birth', person.date_of_birth ? formatWallDateOnly(person.date_of_birth) : '-'],
        ['Started', person.date_started ? formatWallDateOnly(person.date_started) : '-'],
        [personType === 'passenger' ? 'Passenger ID' : 'Employee ID', dash(person.employee_id || person.passenger_id)],
        ['License', `${dash(person.license_number)}${person.license_expiry ? ` (expires ${formatWallDateOnly(person.license_expiry)})` : ''}`],
        ['ID Beacon', person.personal_beacon ? (person.personal_beacon.asset_name || person.personal_beacon.name || person.personal_beacon.imei) : 'None'],
        ['RFID / iButton', person.rfid_ibutton_card ? (person.rfid_ibutton_card.label || person.rfid_ibutton_card.card_value) : (person.credential_number || 'None')],
        ['Current vehicle', binding ? `${bindingLabel} ${binding.vehicle_name || 'vehicle'}` : '-'],
        ['Emergency contact', (person.emergency_contact_name || person.emergency_contact_phone) ? `${dash(person.emergency_contact_name)} ${dash(person.emergency_contact_phone)}` : '-'],
      ],
    }];
    if (behaviour) {
      sections.push({
        heading: 'Driving Behaviour — Last 30 Days', ...two,
        rows: [
          ['Score', hbRatingP === 'not_rated' ? 'Not yet rated' : `${behaviour.score}${hbRatingP === 'low_confidence' ? ' (low confidence)' : ''}`],
          ['Distance', `${behaviour.km} km`], ['Allocated trips', String(behaviour.trips)],
          ['Harsh braking', String(behaviour.harsh_braking || 0)],
          ['Harsh acceleration', String(behaviour.harsh_acceleration || 0)],
          ['Harsh cornering', String(behaviour.harsh_cornering || 0)],
          ['Events per 100 km', String(behaviour.events_per_100km)],
        ],
      });
      if ((behaviour.recent || []).length) {
        sections.push({
          heading: 'Recent Harsh Events',
          columns: [{ label: 'Type', weight: 1 }, { label: 'Vehicle', weight: 1.4 }, { label: 'Time', weight: 1 }],
          rows: behaviour.recent.map((e) => [HB_LABELS[e.type] || e.type, e.vehicle_name || '-', fmtDT(e.triggered_at)]),
        });
      }
    } else if (tripStats) {
      sections.push({ heading: 'Activity — Last 30 Days', ...two, rows: [['Distance', `${tripStats.km} km`], ['Trips', String(tripStats.trips)]] });
    }
    if (recent.entries.length) {
      sections.push({
        heading: 'Recent Log Entries',
        columns: [{ label: 'Vehicle', weight: 1.4 }, { label: 'Start', weight: 1 }, { label: 'End', weight: 1 }, { label: 'Distance', weight: 0.7 }],
        rows: recent.entries.map((e) => {
          const st = e.started_at || e.start_time; const en = e.ended_at || e.end_time;
          return [e.vehicle_name || e.vehicle || '-', fmtDT(st), en ? fmtDT(en) : 'ongoing', e.distance_km != null ? `${Number(e.distance_km).toFixed(1)} km` : '-'];
        }),
      });
    }
    if (attachments.length) {
      sections.push({
        heading: 'Documents',
        columns: [{ label: 'Document', weight: 2 }, { label: 'Type', weight: 1 }, { label: 'Expiry', weight: 1 }],
        rows: attachments.map((a) => [a.name || 'Attachment', a.doc_type || '-', a.expiry_date ? formatWallDateOnly(a.expiry_date) : '-']),
      });
    }
    if (audit.entries.length) {
      sections.push({
        heading: 'Audit Trail (latest)',
        columns: [{ label: 'Action', weight: 1 }, { label: 'When', weight: 1 }, { label: 'By', weight: 1.3 }],
        rows: audit.entries.map((e) => [AUDIT_LABELS[e.action] || e.action, fmtDT(e.at), e.actor_email || '-']),
      });
    }
    return sections;
  };
  const exportDashboard = (fmt) => {
    const title = `${person.full_name} — Person Dashboard`;
    const subtitle = `${personType.charAt(0).toUpperCase()}${personType.slice(1)} · exported ${formatDT(Date.now())}`;
    const fname = `${safeName(person.full_name)}-dashboard.${fmt}`;
    if (fmt === 'pdf') {
      exportDashboardPdf(title, subtitle, buildExportSections(), fname).catch(() => toast.error('Export failed'));
      return;
    }
    const opts = {
      kpis: [
        { label: 'Distance (30d)', value: `${(behaviour?.km ?? tripStats?.km) ?? 0} km`, accent: 'blue' },
        { label: 'Trips (30d)', value: String((behaviour?.trips ?? tripStats?.trips) ?? 0), accent: 'purple' },
        { label: 'Documents', value: String(attachments.length), accent: 'green' },
        { label: 'Status', value: person.status ? `${person.status.charAt(0).toUpperCase()}${person.status.slice(1)}` : '—', accent: 'amber' },
      ],
      score: behaviour ? {
        heading: 'Driving Behaviour — Last 30 Days',
        value: behaviour.score,
        rating: hbRatingP,
        note: hbRatingP === 'low_confidence' ? `Low confidence — ${behaviour.km ?? 0} km driven in the last 30 days.` : null,
        legend: [
          { label: 'Harsh Braking', count: behaviour.harsh_braking || 0, color: '#ef4444' },
          { label: 'Harsh Acceleration', count: behaviour.harsh_acceleration || 0, color: '#f59e0b' },
          { label: 'Harsh Cornering', count: behaviour.harsh_cornering || 0, color: '#8b5cf6' },
        ],
      } : null,
      badge: binding ? {
        text: `${bindingLabel} ${binding.vehicle_name || 'vehicle'}`,
        color: binding.role === 'driving' ? '#2563eb' : '#0d9488',
        icon: 'car',
      } : null,
    };
    exportDashboardHtml(title, subtitle, buildExportSections(), fname, opts).catch(() => toast.error('Export failed'));
  };

  const scoreKpiColor = behaviour == null || behaviour.score == null || hbRatingP === 'not_rated' ? 'bg-slate-500'
    : behaviour.score >= 85 ? 'bg-emerald-500'
    : behaviour.score >= 60 ? 'bg-amber-500'
    : 'bg-red-500';
  const hbScoreTextCls = behaviour?.score == null || hbRatingP === 'not_rated' ? 'text-muted-foreground'
    : behaviour.score >= 85 ? 'text-emerald-600 dark:text-emerald-400'
    : behaviour.score >= 60 ? 'text-amber-600 dark:text-amber-400'
    : 'text-red-600 dark:text-red-400';
  const hbTotal = behaviour?.total || 0;
  const hbDonut = hbTotal === 0
    ? [{ name: 'Clean', key: 'clean', value: 1 }]
    : ['harsh_braking', 'harsh_acceleration', 'harsh_cornering']
        .map((k) => ({ name: HB_LABELS[k], key: k, value: behaviour?.[k] || 0 }))
        .filter((d) => d.value > 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 w-screen h-[100dvh] max-w-none rounded-none sm:rounded-lg sm:w-[94vw] sm:max-w-6xl sm:h-[92vh] flex flex-col overflow-hidden" data-testid="person-profile-drawer">
        <DialogHeader className="px-4 pt-4 pb-3 border-b shrink-0 text-left">
          <div className="flex items-start gap-3">
            {photoUrl ? (
              <button type="button" onClick={() => setViewerOpen(true)} title="View photo" className="block shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2563eb]" data-testid="profile-drawer-photo-btn">
                <img src={photoUrl} alt={person.full_name} className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border cursor-pointer" data-testid="profile-drawer-photo" />
              </button>
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg bg-[#2563eb]/10 text-[#2563eb] flex items-center justify-center text-xl font-semibold shrink-0" data-testid="profile-drawer-photo-placeholder">{initials}</div>
            )}
            <div className="min-w-0 flex-1">
              <DialogTitle className="flex flex-wrap items-center gap-2" data-testid="profile-drawer-name">
                {person.full_name}
                {(person.employee_id || person.passenger_id) && (
                  <span className="text-sm font-normal text-muted-foreground font-mono">{person.employee_id || person.passenger_id}</span>
                )}
                <Badge variant="outline" className={`text-xs font-normal capitalize ${person.status === 'active' ? 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400'}`} data-testid="profile-drawer-status">
                  {person.status || 'inactive'}
                </Badge>
              </DialogTitle>
              <DialogDescription className="text-xs capitalize">{personType}{person.department ? ` — ${person.department}` : ''}{person.role ? ` / ${person.role}` : ''}</DialogDescription>
              <div className="flex flex-wrap items-center gap-1.5 pt-1.5">
                {!person.linked_user_id && <SignupBadge status={person.chat_invite_status} />}
                {person.driver_eligible && <Badge variant="outline" className="gap-1 bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300"><Car className="w-3 h-3" /> Driver eligible</Badge>}
                {person.linked_user_id && (
                  <Badge variant="outline" className="gap-1 bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/40 dark:text-sky-300" data-testid="profile-drawer-linked-badge">
                    <Link2 className="w-3 h-3" /> Linked account
                  </Badge>
                )}
                {(() => {
                  const gid = person[`${personType}_group`];
                  const g = gid && groups.find((x) => x.id === gid);
                  return g ? (
                    <Badge variant="outline" className="gap-1" style={{ color: resolveGroupTextColor(g.color || '#475569'), borderColor: g.color || '#475569' }} data-testid="profile-drawer-group-badge">
                      <Layers className="w-3 h-3" /> {g.name}
                    </Badge>
                  ) : null;
                })()}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 pt-2">
            <Button size="sm" variant="outline" className="h-7" onClick={onLogBook} data-testid="profile-drawer-logbook-btn">
              <BookOpen className="w-3.5 h-3.5 mr-1.5" /> Log Book
            </Button>
            {person.personal_beacon_id && (
              <Button size="sm" variant="outline" className="h-7" onClick={onLocation} data-testid="profile-drawer-location-btn">
                <MapPin className="w-3.5 h-3.5 mr-1.5" /> Last Location
              </Button>
            )}
            {person.personal_beacon_id && onObservedHistory && (
              <Button size="sm" variant="outline" className="h-7" onClick={onObservedHistory} data-testid="profile-drawer-observed-history-btn">
                <Clock className="w-3.5 h-3.5 mr-1.5" /> Observed Beacon History
              </Button>
            )}
            <Button size="sm" variant="outline" className="h-7" onClick={onEdit} data-testid="profile-drawer-edit-btn">
              <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="h-7" data-testid="profile-drawer-export-btn">
                  <Upload className="w-3.5 h-3.5 mr-1.5" /> Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => exportDashboard('pdf')} data-testid="profile-drawer-export-pdf">PDF</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => exportDashboard('html')} data-testid="profile-drawer-export-html">HTML</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {expInfo && (
              <span className={`${chipCls} ${expInfo.cls}`} data-testid="profile-drawer-license-expiry">
                <IdCard className="w-3.5 h-3.5" /> {expInfo.label}
              </span>
            )}
            <span className="ml-auto flex items-center gap-2">
              {lastUpdated && (
                <span className="text-[10px] text-muted-foreground" title={formatDT(lastUpdated)} data-testid="profile-drawer-updated-ago">Updated {formatTimeAgo(lastUpdated)}</span>
              )}
              <Button size="sm" variant="outline" className="h-7" onClick={() => setRefreshNonce((n) => n + 1)} disabled={recent.loading} title="Refresh data (also auto-refreshes every 60s)" data-testid="profile-drawer-refresh-button">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${recent.loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
              {canMessage && (
                <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={openChat} title={`Chat with ${person.full_name}`} data-testid="profile-drawer-chat-btn">
                  <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> Chat
                </Button>
              )}
            </span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* KPI cards — coloured icon tiles (Vehicle Dashboard style). Behaviour KPIs for drivers/staff only. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="profile-drawer-kpis">
            {behaviour ? (
              <>
                <KpiCard label="Score (30d)" value={hbRatingP === 'not_rated' ? 'Not rated' : (behaviour.score ?? '—')} sub={hbRatingP === 'low_confidence' ? 'Low confidence' : (hbRatingP === 'not_rated' ? `Under ${behaviour.thresholds?.min_rated_km ?? 50} km driven` : undefined)} icon={Gauge} color={scoreKpiColor} testId="profile-drawer-kpi-score" />
                <KpiCard label="Harsh Events (30d)" value={behaviour.total ?? 0} icon={AlertTriangle} color="bg-red-500" testId="profile-drawer-kpi-events" />
                <KpiCard label="Distance (30d)" value={`${behaviour.km ?? 0} km`} icon={Route} color="bg-emerald-500" testId="profile-drawer-kpi-distance" />
                <KpiCard label="Trips (30d)" value={behaviour.trips ?? 0} sub="Allocated trips" icon={Car} color="bg-purple-500" testId="profile-drawer-kpi-trips" />
              </>
            ) : (
              <>
                <KpiCard label="Distance (30d)" value={`${tripStats?.km ?? 0} km`} icon={Route} color="bg-emerald-500" testId="profile-drawer-kpi-distance" />
                <KpiCard label="Trips (30d)" value={tripStats?.trips ?? 0} sub={personType === 'passenger' ? 'Rides in vehicles' : 'Vehicle trips'} icon={Car} color="bg-purple-500" testId="profile-drawer-kpi-trips" />
                <KpiCard label="Documents" value={attachments.length} icon={Paperclip} color="bg-blue-500" testId="profile-drawer-kpi-documents" />
              </>
            )}
          </div>

          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            <SectionCard icon={IdCard} title="Contact & Details" testId="profile-drawer-contact">
              <div className="space-y-1.5">
                <Row icon={Mail} label="Email" value={person.email} href={person.email ? `mailto:${person.email}` : undefined} testid="profile-row-email" />
                <Row icon={Phone} label="Phone" value={person.phone} href={person.phone ? `tel:${person.phone}` : undefined} testid="profile-row-phone" />
                <Row icon={MapPin} label="Address" value={person.address} />
                <Row icon={Calendar} label="Date of birth" value={person.date_of_birth ? formatWallDateOnly(person.date_of_birth) : null} />
                <Row icon={Calendar} label="Started" value={person.date_started ? formatWallDateOnly(person.date_started) : null} />
                {personType === 'passenger'
                  ? <Row icon={IdCard} label="Passenger ID" value={person.passenger_id} />
                  : <Row icon={IdCard} label="Employee ID" value={person.employee_id} />}
                {(person.license_number || person.license_expiry) && (
                  <div className="flex items-start gap-2 text-sm">
                    <IdCard className="w-3.5 h-3.5 mt-0.5 text-muted-foreground shrink-0" />
                    <span className="text-muted-foreground w-28 shrink-0">License</span>
                    <span className="min-w-0">
                      {person.license_number || '-'}{person.license_expiry ? ` (expires ${formatWallDateOnly(person.license_expiry)})` : ''}
                      {expInfo && (
                        <span title={expInfo.label} className="inline-flex align-middle ml-1.5" data-testid="profile-drawer-license-warning">
                          <AlertTriangle className={`w-4 h-4 ${/expired/i.test(expInfo.label) ? 'text-red-500' : 'text-amber-500'}`} />
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </SectionCard>

            <SectionCard icon={CreditCard} title="Credentials" testId="profile-drawer-credentials">
              <div className="space-y-1.5">
                <Row icon={Bluetooth} label="ID Beacon" value={person.personal_beacon ? (person.personal_beacon.asset_name || person.personal_beacon.name || person.personal_beacon.imei) : 'None'} testid="profile-row-beacon" />
                {person.personal_beacon?.low_battery === true && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1 ml-5"><AlertTriangle className="w-3 h-3" /> Beacon battery low</p>
                )}
                <Row icon={CreditCard} label="RFID / iButton" value={person.rfid_ibutton_card ? (person.rfid_ibutton_card.label || person.rfid_ibutton_card.card_value) : (person.credential_number || 'None')} testid="profile-row-card" />
              </div>
              {binding && (
                <div className="mt-3 pt-3 border-t" data-testid="profile-drawer-current-vehicle">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 flex items-center gap-1"><Car className="w-3 h-3" /> Current vehicle</p>
                  <button
                    type="button"
                    onClick={openVehicle}
                    title={binding.vehicle_device_id ? 'Open Vehicle Dashboard' : undefined}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white transition-colors ${binding.role === 'driving' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    data-testid="profile-drawer-current-vehicle-badge"
                  >
                    <Car className="w-3.5 h-3.5" /> {bindingLabel} {binding.vehicle_name || 'vehicle'}
                    {binding.vehicle_device_id && <ExternalLink className="w-3 h-3 opacity-80" />}
                  </button>
                </div>
              )}
              <div className="mt-3 pt-3 border-t space-y-1.5" data-testid="profile-drawer-emergency">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Emergency contact</p>
                {(person.emergency_contact_name || person.emergency_contact_phone) ? (
                  <>
                    <Row icon={ShieldAlert} label="Name" value={person.emergency_contact_name} />
                    <Row icon={Phone} label="Phone" value={person.emergency_contact_phone} />
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">None recorded.</p>
                )}
              </div>
            </SectionCard>
          </div>

          <div className="grid gap-4 grid-cols-1 lg:grid-cols-2">
            {personType !== 'passenger' && behaviour && (
              <SectionCard
                icon={Gauge}
                title="Driving Behaviour — Last 30 Days"
                testId="profile-drawer-behaviour"
                right={
                  hbRatingP === 'not_rated' ? (
                    <span className="flex items-center gap-1.5">
                      <DrivingBehaviourHelp />
                      <Badge variant="outline" className="text-xs font-bold bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400" data-testid="profile-drawer-behaviour-score">Not yet rated</Badge>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5">
                      <DrivingBehaviourHelp />
                      {hbRatingP === 'low_confidence' && (
                        <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400" data-testid="profile-drawer-behaviour-confidence">Low confidence</Badge>
                      )}
                      <Badge variant="outline" className={`text-xs font-bold ${behaviourScoreCls(behaviour.score)}`} data-testid="profile-drawer-behaviour-score">
                        Score {behaviour.score}
                      </Badge>
                    </span>
                  )
                }
              >
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-[150px_1fr] gap-3 items-center">
                    {/* Behaviour Score donut — same style as the Vehicle Dashboard */}
                    <div className="relative h-36" data-testid="profile-drawer-behaviour-donut">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie data={hbDonut} dataKey="value" nameKey="name" innerRadius={34} outerRadius={55} paddingAngle={hbDonut.length > 1 ? 2 : 0} startAngle={90} endAngle={-270}>
                            {hbDonut.map((s) => <Cell key={s.key} fill={s.key === 'clean' ? '#10b981' : HB_COLORS[s.key]} />)}
                          </Pie>
                          {hbTotal > 0 && <ReTooltip {...TT_PROPS} formatter={(v, n) => [v, n]} />}
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                        <span className={`text-xl font-bold leading-none ${hbScoreTextCls}`} data-testid="profile-drawer-behaviour-donut-score">{hbRatingP === 'not_rated' ? '—' : (behaviour.score ?? '—')}</span>
                        <span className="text-[10px] text-muted-foreground mt-0.5">{hbRatingP === 'not_rated' ? 'Not rated' : 'Score'}</span>
                      </div>
                    </div>
                    <div className="space-y-2 min-w-0">
                      {hbTotal === 0 ? (
                        <p className="text-sm text-emerald-600 dark:text-emerald-400" data-testid="profile-drawer-behaviour-clean">
                          No harsh driving events in the last 30 days.
                        </p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 text-center">
                          {['harsh_braking', 'harsh_acceleration', 'harsh_cornering'].map((k) => (
                            <div key={k} className="rounded-lg px-2 py-1.5 shadow-sm" style={{ backgroundColor: HB_COLORS[k] }} data-testid={`profile-drawer-behaviour-${k}`}>
                              <p className="text-lg font-bold leading-tight text-white">{behaviour[k] || 0}</p>
                              <p className="text-[10px] leading-tight text-white/90">{HB_LABELS[k]}</p>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-[11px] text-muted-foreground" data-testid="profile-drawer-behaviour-rate">
                        {behaviour.total} harsh event{behaviour.total === 1 ? '' : 's'} · {behaviour.km} km over {behaviour.trips} allocated trip{behaviour.trips === 1 ? '' : 's'} · {behaviour.events_per_100km} per 100 km — across all vehicles driven
                      </p>
                      {hbRatingP !== 'rated' && (
                        <p className="text-[11px] text-muted-foreground" data-testid="profile-drawer-behaviour-rating-note">
                          {hbRatingP === 'not_rated'
                            ? `Not yet rated — under the ${behaviour.thresholds?.min_rated_km ?? 50} km minimum rated distance (change in Settings).`
                            : `Low confidence — under ${behaviour.thresholds?.low_confidence_km ?? 200} km driven in the window (change in Settings).`}
                        </p>
                      )}
                    </div>
                  </div>
                  {(behaviour.recent || []).length > 0 && (
                    <div className="divide-y rounded-md border">
                      {behaviour.recent.slice(0, 5).map((e, i) => (
                        <div key={e.id || i} className="text-xs px-3 py-2 flex items-center justify-between gap-2 min-w-0" data-testid={`profile-drawer-behaviour-event-${i}`}>
                          <span className="truncate font-medium min-w-0 flex-1">{HB_LABELS[e.type] || e.type} — {e.vehicle_name || 'Vehicle'}</span>
                          <span className="text-muted-foreground text-right shrink-0 whitespace-nowrap">{fmtDT(e.triggered_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            <SectionCard
              icon={Route}
              title="Recent Log Entries"
              testId="profile-drawer-trips"
              className={personType === 'passenger' || !behaviour ? 'lg:col-span-2' : ''}
              right={<Button variant="link" size="sm" className="h-5 px-0 text-xs" onClick={onLogBook} data-testid="profile-drawer-open-logbook">Full log book</Button>}
            >
              {recent.loading ? (
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</p>
              ) : recent.entries.length === 0 ? (
                <p className="text-xs text-muted-foreground">No log entries yet.</p>
              ) : (
                <div className="divide-y rounded-md border">
                  {recent.entries.map((e, i) => {
                    const st = e.started_at || e.start_time;
                    const en = e.ended_at || e.end_time;
                    const isConflict = e.credential_source === 'driver_session_conflict';
                    if (isConflict) {
                      // Mid-trip scan that was correctly ignored — an event, not a trip (never "ongoing").
                      return (
                        <div key={i} className="text-xs px-3 py-2 space-y-0.5 min-w-0" data-testid={`profile-drawer-trip-${i}`}>
                          <div className="flex items-center justify-between gap-2 min-w-0">
                            <span className="truncate font-medium min-w-0 flex-1">{e.vehicle_name || e.vehicle || '-'}</span>
                            <Badge variant="outline" className="shrink-0 text-[10px] text-amber-600 border-amber-300 dark:text-amber-400 dark:border-amber-800" data-testid={`profile-drawer-trip-conflict-${i}`}>
                              Scan ignored
                            </Badge>
                          </div>
                          <div className="text-muted-foreground truncate" title={e.summary || ''}>{e.summary || 'Scanned mid-trip while another driver was active'}</div>
                          <div className="text-muted-foreground">{fmtDT(e.timestamp)}</div>
                        </div>
                      );
                    }
                    return (
                      <div key={i} className="text-xs px-3 py-2 space-y-0.5 min-w-0" data-testid={`profile-drawer-trip-${i}`}>
                        <div className="flex items-center justify-between gap-2 min-w-0">
                          <span className="truncate font-medium min-w-0 flex-1">{e.vehicle_name || e.vehicle || '-'}</span>
                          <span className="flex items-center gap-2.5 shrink-0">
                            {e.distance_km != null && <span className="inline-flex items-center gap-1" data-testid={`profile-drawer-trip-distance-${i}`}><Route className="w-3 h-3" /> {Number(e.distance_km).toFixed(1)} km</span>}
                            {st && en && <span className="inline-flex items-center gap-1" data-testid={`profile-drawer-trip-duration-${i}`}><Clock className="w-3 h-3" /> {formatDuration(Math.max(0, Math.round((parseTs(en) - parseTs(st)) / 1000)))}</span>}
                          </span>
                        </div>
                        <div className="text-muted-foreground">
                          {st
                            ? <>{fmtDT(st)}{en ? ` → ${fmtDT(en)}` : ' → ongoing'}</>
                            : fmtDT(en || e.timestamp)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </SectionCard>
          </div>

          {/* Documents — read-only list (view/download); uploads live in the Edit dialog. Hidden when empty. */}
          {attachments.length > 0 && (
            <SectionCard icon={Paperclip} title={`Documents (${attachments.length})`} testId="profile-drawer-documents">
              <ReadOnlyAttachmentsList attachments={attachments} testPrefix="profile-drawer-doc" />
              <p className="text-[11px] text-muted-foreground mt-2">View or download only — add or edit documents in the Edit dialog.</p>
            </SectionCard>
          )}

          <SectionCard icon={History} title="Audit Trail" testId="profile-drawer-audit">
            {audit.loading ? (
              <p className="text-xs text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</p>
            ) : audit.entries.length === 0 ? (
              <p className="text-xs text-muted-foreground">No recorded changes yet.</p>
            ) : (
              <>
                <div className="divide-y rounded-md border">
                  {audit.entries.map((e, i) => (
                    <div key={e.id || i} className="text-xs px-3 py-2 min-w-0" data-testid={`profile-drawer-audit-${i}`}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium">{AUDIT_LABELS[e.action] || e.action}</span>
                        <span className="text-muted-foreground whitespace-nowrap">{fmtDT(e.at)}</span>
                      </div>
                      {e.changes?.length > 0 && (
                        <p className="text-muted-foreground break-words mt-0.5">{e.changes.join(', ')}</p>
                      )}
                      <p className="text-muted-foreground truncate mt-0.5" title={e.actor_email || undefined}>by {e.actor_email || '-'}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-2">
                  <PaginationFooter
                    page={auditPage}
                    setPage={setAuditPage}
                    totalItems={audit.total}
                    itemsPerPage={auditPageSize}
                    setItemsPerPage={setAuditPageSize}
                    testIdPrefix="profile-audit"
                    scrollOnChange={false}
                    disabled={audit.loading}
                  />
                </div>
              </>
            )}
          </SectionCard>
        </div>

        <Dialog open={viewerOpen && !!photoUrl} onOpenChange={setViewerOpen}>
          <DialogContent className="max-w-xl" data-testid="profile-photo-viewer">
            <DialogHeader>
              <DialogTitle>{person.full_name}</DialogTitle>
              <DialogDescription>Profile photo</DialogDescription>
            </DialogHeader>
            {photoUrl && <img src={photoUrl} alt={person.full_name} className="w-full max-h-[65vh] object-contain rounded-md border" data-testid="profile-photo-viewer-img" />}
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
